import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChange,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { SelectItem } from 'primeng/api';
import { Subject, takeUntil } from 'rxjs';
import { supportedLanguages } from '../../i18n.constants';

type LanguageOption = {
  code: string;
  name: string;
};

type LanguageDropdownForm = {
  selectedLanguage: FormControl<LanguageOption>;
};

interface LanguageDropdownChanges extends SimpleChanges {
  currentLanguage: SimpleChange;
}

@Component({
  selector: 'payment-language-dropdown',
  templateUrl: './language-dropdown.component.html',
  styleUrls: ['./language-dropdown.component.scss'],
})
export class LanguageDropdownComponent implements OnInit, OnChanges {
  @Input() currentLanguage: string;

  @Output() languageSelected = new EventEmitter<string>();

  form: FormGroup<LanguageDropdownForm>;
  languageDropdownOptions: SelectItem<LanguageOption>[];

  private destroy$ = new Subject<boolean>();

  constructor(
    private fb: FormBuilder,
    private translateService: TranslateService,
  ) {}

  ngOnChanges(changes: LanguageDropdownChanges): void {
    if (changes.currentLanguage && changes.currentLanguage.currentValue) {
      this.createLanguageDropdownOptions();
      if (!changes.currentLanguage.firstChange) {
        this.form.controls.selectedLanguage.setValue(
          this.getLanguageOptionForSelectedLanguage(),
          { emitEvent: false },
        );
      }
    }
  }

  ngOnInit() {
    this.form = this.fb.group<LanguageDropdownForm>({
      selectedLanguage: this.fb.control<LanguageOption>(
        this.getLanguageOptionForSelectedLanguage(),
      ),
    });

    this.form.controls.selectedLanguage.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedLanguage: LanguageOption) => {
        this.languageSelected.emit(selectedLanguage.code);
      });
  }

  private getLanguageOptionForSelectedLanguage() {
    return this.languageDropdownOptions.find(
      (x) => x.value.code === this.currentLanguage,
    )?.value;
  }

  private createLanguageDropdownOptions() {
    this.languageDropdownOptions = supportedLanguages
      .map((language) => {
        const value: LanguageOption = {
          code: language,
          name: this.translateService.instant(
            `ARC_PAYMENT_LANGUAGE_DROPDOWN_LABEL_${language
              .toUpperCase()
              .replace('-', '_')}`,
          ) as string,
        };
        const item: SelectItem<LanguageOption> = {
          value,
          label: value.name,
        };
        return item;
      })
      .sort((a, b) => {
        if (a.label < b.label) {
          return -1;
        }
        if (a.label > b.label) {
          return 1;
        }
        return 0;
      });
  }
}
